import React, { useEffect } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

/**
 * Esri ArcGIS "Canvas" basemaps matched to light/dark Situation Room theme.
 * Free, no API key required (unlike CARTO's basemaps.cartocdn.com, which
 * started requiring one — see carto.com/basemap/api_key). Base layer is the
 * muted background; the matching *_LABELS_URL reference layer adds country/
 * city labels and borders on top of it.
 */
export const TILE_URL = {
  light: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
  dark: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
} as const;

export const TILE_LABELS_URL = {
  light: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
  dark: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
} as const;

export const RISK_COLORS = {
  low: "#16a34a",
  moderate: "#d97706",
  high: "#dc2626",
  kenya: "#0d9488",
} as const;

export const TYPE_COLORS = {
  Air: "#64748b",
  Sea: "#2563eb",
  Land: "#d97706",
} as const;

/**
 * Attribution intentionally hidden to match the client reference report HTML.
 */
export const ATTRIBUTION_CSS = `
.leaflet-control-attribution { display: none !important; }
.dash-map-tip {
  background: rgba(15, 31, 53, 0.92) !important;
  border: 1px solid rgba(255,255,255,0.12) !important;
  border-radius: 10px !important;
  color: rgba(255,255,255,0.92) !important;
  box-shadow: 0 8px 24px rgba(0,0,0,.35) !important;
  padding: 8px 10px !important;
  font: 12px/1.35 Inter, Helvetica, sans-serif !important;
}
.dash-map-tip::before { border-top-color: rgba(15,31,53,0.92) !important; }
.leaflet-tooltip-top.dash-map-tip::before { border-top-color: rgba(15,31,53,0.92) !important; }
`;

/**
 * Reference-report 3D pushpin: metal needle + radial-gradient ball head.
 * Color variants for Air / Sea / Land on the entry-points map.
 */
export const PUSHPIN_CSS = `
.dash-pushpin {
  position: relative;
  width: 28px;
  height: 40px;
  transform: rotate(14deg);
  transform-origin: 50% 100%;
  pointer-events: auto;
}
.dash-pushpin__head {
  width: 22px;
  height: 22px;
  margin: 0 auto;
  border-radius: 50%;
  background: radial-gradient(circle at 32% 28%, #ff8a80 0%, #e53935 42%, #b71c1c 78%, #7f0000 100%);
  box-shadow:
    inset -2px -2px 5px rgba(0,0,0,.28),
    inset 2px 2px 4px rgba(255,255,255,.25),
    0 2px 6px rgba(0,0,0,.35);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font: 700 9px/1 'JetBrains Mono', 'Fira Code', monospace;
  transform: rotate(-14deg);
  letter-spacing: -0.3px;
}
.dash-pushpin__needle {
  width: 3px;
  height: 16px;
  margin: -1px auto 0;
  background: linear-gradient(90deg, #8d8d8d 0%, #f5f5f5 45%, #616161 100%);
  border-radius: 0 0 1px 1px;
  box-shadow: 0 1px 2px rgba(0,0,0,.25);
}
.dash-pushpin--plain .dash-pushpin__head { font-size: 0; }
.dash-pushpin--air .dash-pushpin__head {
  background: radial-gradient(circle at 32% 28%, #cbd5e1 0%, #64748b 45%, #334155 100%);
}
.dash-pushpin--sea .dash-pushpin__head {
  background: radial-gradient(circle at 32% 28%, #93c5fd 0%, #2563eb 45%, #1e3a8a 100%);
}
.dash-pushpin--land .dash-pushpin__head {
  background: radial-gradient(circle at 32% 28%, #fcd34d 0%, #d97706 45%, #92400e 100%);
}
`;

let stylesInjected = false;
export function ensureMapStyles() {
  if (stylesInjected || typeof document === "undefined") return;
  const el = document.createElement("style");
  el.setAttribute("data-dash-map", "1");
  el.textContent = ATTRIBUTION_CSS + PUSHPIN_CSS;
  document.head.appendChild(el);
  stylesInjected = true;
}

export function buildPushpinIcon(type?: "Air" | "Sea" | "Land" | "plain"): L.DivIcon {
  ensureMapStyles();
  const variant =
    type === "Air" ? "dash-pushpin--air" :
    type === "Sea" ? "dash-pushpin--sea" :
    type === "Land" ? "dash-pushpin--land" :
    "dash-pushpin--plain";
  return L.divIcon({
    className: "dash-pushpin-wrap",
    html: `<div class="dash-pushpin ${variant}"><div class="dash-pushpin__head"></div><div class="dash-pushpin__needle"></div></div>`,
    iconSize: [28, 40],
    iconAnchor: [14, 40],
    popupAnchor: [0, -36],
  });
}

export function buildOriginPinIcon(count: number, _risk?: string): L.DivIcon {
  ensureMapStyles();
  const label = count > 999 ? "999+" : String(count);
  // Blue volume theme (screening count) — risk coloring intentionally unused
  const head =
    "radial-gradient(circle at 32% 28%, #93c5fd 0%, #2563eb 45%, #1e3a8a 100%)";
  return L.divIcon({
    className: "dash-pushpin-wrap",
    html: `<div class="dash-pushpin"><div class="dash-pushpin__head" style="background:${head}">${label}</div><div class="dash-pushpin__needle"></div></div>`,
    iconSize: [28, 40],
    iconAnchor: [14, 40],
    popupAnchor: [0, -36],
  });
}

/** Fix blank-gap bug: Leaflet sizes once on mount before card layout settles. */
export const InvalidateSizeOnMount: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    const bump = () => map.invalidateSize({ animate: false });
    const raf = requestAnimationFrame(bump);
    const t = window.setTimeout(bump, 400);
    window.addEventListener("resize", bump);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t);
      window.removeEventListener("resize", bump);
    };
  }, [map]);
  return null;
};

export type FitBoundsProps = {
  positions: [number, number][];
  padding?: number;
  maxZoom?: number;
};

export const FitBounds: React.FC<FitBoundsProps> = ({
  positions,
  padding = 70,
  maxZoom = 7,
}) => {
  const map = useMap();
  useEffect(() => {
    if (!positions.length) return;
    const bounds = L.latLngBounds(positions);
    map.fitBounds(bounds, { padding: [padding, padding], maxZoom });
  }, [map, positions, padding, maxZoom]);
  return null;
};

/** Approx Kenya bbox — used when GeoJSON has not loaded yet. */
export const KENYA_BOUNDS: [[number, number], [number, number]] = [
  [-4.7, 33.9],
  [5.0, 42.0],
];
